import {
    HttpClient,
    HttpErrorResponse
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import {
    Observable,
    throwError
} from "rxjs";
import {
    catchError,
    distinctUntilChanged,
    map,
    takeUntil,
    tap
} from "rxjs/operators";
import { ToastAlertService } from "src/app/shared/utils/toast.service";
import {
  InvestorTypes,
    OrganizationInformationModel, CorporateInformationResponseModel
} from "../domain";
import { InvestmentInfoModel } from "../domain/investment-types.model";
import { OrgRepresentativeModel } from "../domain/org-representative.model";
import { ApiEndpointService } from "./api-endpoint.service";
import * as ProfileActions from "src/app/core/state/profile/profile.action";
import * as InvestorDashboardAction from 'src/app/core/state/investor-dashboard/investor.dashboard.action';
import { getProfileData } from "../state/profile";
import { IProfileDetails } from "../domain/profile.model";
import { GlobalService } from "./global.service";
import { httpFaultMessage } from 'src/app/shared/utils/http-fault.util';
@Injectable({
    providedIn: "root"
})
export class InvestorService {
    profileData: IProfileDetails;
    /**
     * Constructor.
     */
    constructor(private http: HttpClient, private toastService: ToastAlertService, private store: Store, private globalService: GlobalService) {
      this.store.pipe(select(getProfileData))
      .pipe(distinctUntilChanged()).subscribe((res) => {
        if (res) {
          this.profileData = res;
        }
      })
    }

    getProfileCompleteness() {
      console.log('getting completeness', this.profileData.investorType);
      if(this.profileData.investorType === InvestorTypes.INDIVIDUAL) {
        return this.store.dispatch(new InvestorDashboardAction.GetIndividualProfileCompleteness());
      }
      return this.store.dispatch(new InvestorDashboardAction.GetProfileCompleteness());
    }

    public patchInvestorOrganizationInfo(organizationInfo: OrganizationInformationModel): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.INVESTOR_ORGANIZATION_INFO);
        return this.http.patch(url, organizationInfo).pipe(
                tap(() => {
                  this.getProfileCompleteness()
                }),
                catchError((fault: HttpErrorResponse) => {
                // SC-SAAS-FRONTEND-2G / SAN-1019 -- was mislabeled `loginFault`; this is
                // the investor organization-info save, not a login. Same relabel as the
                // sibling saves below (representativeInfoFault / investmentInfoFault).
                   console.warn(`organizationInfoFault( ${fault.error?.error?.[0] || fault.error?.message || fault.message || fault.status} )`);
                // Fix (2026-08-26): a 400 here is a backend validation
                // rejection (class-validator) -- its raw message (e.g.
                // "organizationTypeId must be a number conforming to the
                // specified constraints") is meant for API debugging, not
                // end users. Show a plain-English message instead; backend
                // validation itself is unchanged. Any other status (network,
                // 500, etc.) still falls back to the existing generic
                // extractor/message.
                const message = fault.status === 400
                  ? 'Please fill in all the required information before you can move ahead.'
                  : this.globalService.getErrorMessage(fault, 'Error while saving organization info!');
                // SAN-1019 -- `message` was computed above but never used (the toast
                // re-ran getErrorMessage() with a typo'd fallback), so the intended
                // plain-English 400 message was never shown.
                this.toastService.showToast(message, 'error');
                return throwError(() => fault);
            })
        );
    }

    public patchInvestorOrgRepresentativeInfo(organizationInfo: OrgRepresentativeModel): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.INVESTOR_REPRESENTATIVE_INFO);
        return this.http.patch(url, organizationInfo).pipe(
                tap(() => {
                  this.getProfileCompleteness()
                }),
                catchError((fault: HttpErrorResponse) => {
                console.warn(`representativeInfoFault( ${fault.error?.message || fault.message || fault.status} )`);
                // Fix (2026-08-26): a 400 here is a backend validation
                // rejection (class-validator) -- its raw message (e.g.
                // "organizationTypeId must be a number conforming to the
                // specified constraints") is meant for API debugging, not
                // end users. Show a plain-English message instead; backend
                // validation itself is unchanged. Any other status (network,
                // 500, etc.) still falls back to the existing generic
                // extractor/message.
                const message = fault.status === 400
                  ? 'Please fill in all the required information before you can move ahead.'
                  : this.globalService.getErrorMessage(fault, 'Error while saving organization info!');
                this.toastService.showToast(message, 'error');
                return throwError(() => fault);
            })
        );
    }

    public patchInvestorOrgInvestmentInfo(organizationInfo: InvestmentInfoModel): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.INVESTOR_INVESTMENT_INFO);
        return this.http.patch(url, organizationInfo).pipe(
                tap(() => {
                  this.getProfileCompleteness()
                }),
                catchError((fault: HttpErrorResponse) => {
                console.warn(`investmentInfoFault( ${fault.error?.message || fault.message || fault.status} )`);
                // Fix (2026-08-26): a 400 here is a backend validation
                // rejection (class-validator) -- its raw message (e.g.
                // "organizationTypeId must be a number conforming to the
                // specified constraints") is meant for API debugging, not
                // end users. Show a plain-English message instead; backend
                // validation itself is unchanged. Any other status (network,
                // 500, etc.) still falls back to the existing generic
                // extractor/message.
                const message = fault.status === 400
                  ? 'Please fill in all the required information before you can move ahead.'
                  : this.globalService.getErrorMessage(fault, 'Error while saving organization info!');
                this.toastService.showToast(message, 'error');
                return throwError(() => fault);
            })
        );
    }

    public getInvestorOrganizationInfo(): Observable<any> {
        const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.INVESTOR_ORGANIZATION_INFO);
        return this.http.get(url).pipe(
            map((response: any): CorporateInformationResponseModel => {
                return response
            }),
            catchError((fault: HttpErrorResponse) => {
                return throwError(() => fault);
            })
        );
    }

    public uploadLogo(logo: File, refetchProfile = false): Observable<any> {
      const payload = new FormData();
      payload.append('file', logo);
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.INVESTOR_LOGO_UPLOAD);
      console.info(`saving inverstor up logo`, payload);
      return this.http.post(url, payload).pipe(
        tap(() => {
          if(refetchProfile) {
            this.store.dispatch(new ProfileActions.GetProfile());
          }
          this.getProfileCompleteness()
        }),
        catchError((fault: HttpErrorResponse) => {
          this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while uploading logo!'), 'error');
          console.warn(`save inverstor Uplogo( ${httpFaultMessage(fault, `HTTP ${fault?.status}`)} )`);
          return throwError(() => fault);
        })
      );
    }


    public uploadConnectionDocument(file: File): Observable<{ message: string, data: any }> {
      const payload = new FormData();
      payload.append('file', file);
      const url = ApiEndpointService.getEndpoint(ApiEndpointService.ENDPOINT.INVESTOR_CONNECTION_DOC_UPLOAD);
      return this.http.post(url, payload).pipe(
        map((response: any): any => {
          return response;
        }),
        catchError((fault: HttpErrorResponse) => {
          this.toastService.showToast(this.globalService.getErrorMessage(fault, 'Error while saving PitchFile!'), 'error');
          return throwError(() => fault);
        })
      );
    }

    
}
